import { Link } from 'react-router';

import { canReach } from '@shared/chrome/nav';
import { useSessionStore } from '@shared/auth/session';

import { chromeCopy } from './copy';

/**
 * Position in the **URL**, never in the folder tree.
 *
 * The UI/UX pack organises its files into buckets — `taxonomy/`, `moderation/`,
 * `content/` — and none of them is a route. A bucket never becomes a crumb, so
 * the two taxonomy surfaces carry two levels rather than three.
 *
 * An ordered list, and the **first child of `<main>`**: it precedes the page
 * head rather than sitting inside it, because the trail is where the page *is*
 * and the head is what the page is *called*.
 *
 * The separator is the text character `›`, `aria-hidden`, never a drawn arrow —
 * a glyph would need mirroring under `dir="rtl"` and the character follows the
 * paragraph's direction for free.
 *
 * Absent on the console entry, the two system pages, and the three auth screens:
 * the console root has nothing above it, and marking the nearest entry on a
 * fall-through page would announce a location the reader is not at.
 *
 * **A crumb the reader cannot follow renders as text, not as a link.** The
 * console root needs `analytics: operator`, which an operator granted only in
 * `content` does not hold — so «لوحة التشغيل» was a link that bounced straight
 * back to the page it was pressed from. The trail still *names* the level,
 * because the reader's position is unchanged by what they may open; what goes
 * is the promise that pressing it leads anywhere.
 */

export interface Crumb {
  label: string;
  /** Omitted on the current page — the last crumb is text, never a link. */
  to?: string;
}

export function Breadcrumb({ items }: { items: readonly Crumb[] }) {
  const capabilities = useSessionStore((state) => state.capabilities);

  return (
    <nav aria-label={chromeCopy.crumbs.label} className="mt-5 flex flex-wrap items-center gap-2 text-sm">
      <ol className="m-0 flex list-none flex-wrap items-center gap-2 p-0">
        {items.map((crumb, index) => (
          <li key={crumb.label} className="flex items-center gap-2">
            {index > 0 ? (
              <span aria-hidden="true" className="text-ink-faint">
                ›
              </span>
            ) : null}

            {crumb.to && canReach(capabilities, crumb.to) ? (
              <Link
                to={crumb.to}
                className="text-ink-muted no-underline transition-colors duration-fast hover:text-accent-text hover:underline"
              >
                {crumb.label}
              </Link>
            ) : (
              <span aria-current="page" className="text-ink-muted">
                {crumb.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
